/**
 * What a service comes with, on the website (Petal_Some Ug., Oct 2026).
 *
 * A service can carry the same "Comes with / add-ons" list a product does
 * (`products.hireKit`; the till's rules are lib/config/addOns.ts, the
 * product page's lib/config/websiteAddOns.ts). The service card only SAYS
 * what goes with it; booking is unchanged:
 *   Comes with: 1 × Teddy bear, 2 × Chocolates    the free pieces
 *   Extras: Perfume +UGX 50,000                   the priced ones, each
 * A service whose price is on request lists no extras, as the product page
 * offers none for a product sold by quote.
 *
 * The website gets each piece's name, count and price, never the product a
 * piece is linked to nor what it costs the shop.
 */

import { formatUgx } from "./hire";
import { isPaidAddOn, websiteAddOns, type StoredAddOnPiece } from "./websiteAddOns";

/** One piece as a service card receives it. */
export interface ServiceAddOn {
  name: string;
  /** How many go with the service. */
  qty: number;
  /** Each, in the shop's currency; 0 = free. */
  price: number;
}

/** The pieces a service's website row carries: name, count and price, nothing else. */
export function serviceAddOnsFor(
  pieces: ReadonlyArray<Partial<StoredAddOnPiece> | null | undefined> | null | undefined,
): ServiceAddOn[] {
  return websiteAddOns(pieces).map((p) => ({ name: p.name, qty: p.qty, price: p.price }));
}

/**
 * The card's two lines, "" when there is nothing to say: what comes free with
 * the service, and the extras with their prices.
 */
export function serviceAddOnLines(
  pieces: ReadonlyArray<ServiceAddOn | null | undefined> | null | undefined,
  { currency = "UGX", showPrices = true }: { currency?: string; showPrices?: boolean } = {},
): { comesWith: string; extras: string } {
  const named = (pieces || []).filter(
    (p): p is ServiceAddOn => !!p && String(p.name ?? "").trim() !== "" && Math.round(Number(p.qty) || 0) >= 1,
  );
  const count = (p: ServiceAddOn) => Math.round(Number(p.qty));
  const comesWith = named
    .filter((p) => !isPaidAddOn(p))
    .map((p) => `${count(p)} × ${p.name.trim()}`)
    .join(", ");
  const extras = showPrices
    ? named
        .filter(isPaidAddOn)
        .map((p) =>
          count(p) > 1
            ? `${count(p)} × ${p.name.trim()} +${formatUgx(p.price, currency)} each`
            : `${p.name.trim()} +${formatUgx(p.price, currency)}`,
        )
        .join(", ")
    : "";
  return { comesWith, extras };
}
